import { setCookie } from '@visa/lib-cookie';
import {
  getAccessToken,
  getApiKey,
  getComplianceResource,
  getIsFeatureToggleEnabled,
  getUser
} from 'communicator/store/selectors';
import { fetchEnrollSsi, fetchOrchEnrollSsi } from './api/enroll-ssi';
import { fetchSpawnErm, fetchSpawnRememberMe, fetchSpawnSsi } from './api/spawn';
import {
  ErmCookieValue,
  SsiCookieValue,
  getErmEnabledCookieValue,
  getSsiEnabledCookieValue,
  getUsernameFromRememberMe,
  hasValidErmCookie,
  hasValidSsiCookie,
  setErmEnabled,
  setSsiEnabled
} from './cookies';
import { markEnd, markStart } from './instrumentation';

/* This function is used for dealing with checkout completion calls for recognized users.
Below function is a pre-existing code piece that was residing as a block of code in completeCheckoutLegacy.
Moving it to a separate function for better readability and maintainability.
*/

export default async function postCheckoutRecogonisedUser() {
  const accessToken = getAccessToken()?.token as string;
  const user = getUser();
  const apikey = getApiKey();

  if (getComplianceResource('REMEMBER_ME') && !user?.isRecognizedUser) {
    if (getIsFeatureToggleEnabled('orchestratorSsiEnrollApi')) {
      markStart('checkout_integrated_enroll_ssi');
      await fetchOrchEnrollSsi({ accessToken, apikey });
      markEnd('checkout_integrated_enroll_ssi');
    } else if (
      getIsFeatureToggleEnabled('enableSkipMiCookie') &&
      getUser()?.identityType === 'MOBILE_PHONE_NUMBER'
    ) {
      // Skip remember me calls if identityType is mobile phone number
      return;
    } else {
      //Changed the order of api invocation for recognized apis. Priority is given to SSI.
      //Refactor erm and ssi cookie handling later release, since we are not supporting erm recognized flow.
      markStart('checkout_integrated_enroll_ssi');
      const enrollSsiResponse = await fetchEnrollSsi({ accessToken, apikey });
      markEnd('checkout_integrated_enroll_ssi');
      markStart('checkout_integrated_spawn_ssi');
      const spawnSsiResponse = await fetchSpawnSsi({ accessToken });
      markEnd('checkout_integrated_spawn_ssi');

      if (enrollSsiResponse.ok && spawnSsiResponse.ok) {
        setSsiEnabled();
      }

      markStart('checkout_integrated_spawn_remember_me');
      await fetchSpawnRememberMe({ accessToken });
      markEnd('checkout_integrated_spawn_remember_me');
      const maskedUsername = getUsernameFromRememberMe();

      if (maskedUsername) {
        const ermCookieIsValid = hasValidErmCookie();
        const ssiCookieIsValid = hasValidSsiCookie();

        // // update ERM/SSI cookies with maskedEmail if they exist, otherwise drop them
        if (ermCookieIsValid) {
          const ermCookie = getErmEnabledCookieValue() as ErmCookieValue;
          setCookie(
            'ermEnabled',
            JSON.stringify({
              authRef: ermCookie.authRef,
              date: ermCookie.date,
              username: maskedUsername
            })
          );
        } else {
          markStart('checkout_integrated_spawn_erm');
          const ermSpawnResponse = await fetchSpawnErm({ accessToken });
          markEnd('checkout_integrated_spawn_erm');

          if (ermSpawnResponse.ok && ermSpawnResponse.data.issued_tokens?.[0].token) {
            setErmEnabled(ermSpawnResponse.data.issued_tokens[0].token);
          }
        }

        if (ssiCookieIsValid) {
          const ssiCookie = getSsiEnabledCookieValue() as SsiCookieValue;
          setCookie(
            'ssiEnabled',
            JSON.stringify({
              date: ssiCookie.date,
              username: maskedUsername
            })
          );
        }
      }
    }
  }
}
